import React from "react";
import { BeatLoader } from "react-spinners";
import TextInput from "../common/TextInput";
import { FIELD_LIMITS } from "../common/fieldLimits";
import { useTranslation } from "react-i18next";
import {
  OrganizationCreateDto,
  OrganizationResponseDto,
} from "../common/ObjectTypes";
import tailwindClassnames from "../common/TailwindClassnames";
import SelectInput from "../common/SelectInput";
import { Fade, Tooltip } from "@mui/material";

type OrganizationDetailsOverviewProps = {
  organization: OrganizationCreateDto;
  parentOrganizationOptions: OrganizationResponseDto[];
  countries: any[];
  existingOrganization: boolean;
  perms: any[];
  errors: any;
  isSaving: boolean;
  onSelectParentOrg: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSelectCountry: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  onCancel: () => void;
  onSave: (e: React.FormEvent) => void;
  saveDisabled: boolean;
};

const OrganizationDetailsOverview: React.FC<
  OrganizationDetailsOverviewProps
> = ({
  organization,
  parentOrganizationOptions,
  countries,
  existingOrganization,
  perms,
  errors,
  isSaving,
  onSelectParentOrg,
  onSelectCountry,
  onChange,
  onCancel,
  onSave,
  saveDisabled,
}: OrganizationDetailsOverviewProps) => {
  const { t } = useTranslation();
  const canEditOrgDetails = perms.some(
    (permission: any) => permission.name === "EDIT_ORG_DETAILS",
  );

  const countryId = organization.country_id;
  const getCountryByCode = (code: any) => {
    return countries.find((country) => country.code === code);
  };
  const defaultCountry = getCountryByCode(countryId);

  return (
    <div className="w-full">
      <form className="w-full pt-4 text-left mb-4">
        <section className={tailwindClassnames.formSectionCard}>
          <div className="flex flex-wrap text-left -mx-3 mb-6">
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
              <TextInput
                name="org_name"
                label={t("Organization Name") + "*"}
                placeholder=""
                value={organization.org_name}
                error={errors.org_name}
                disabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                className={
                  canEditOrgDetails || !existingOrganization
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                type="text"
                initialFocus={true}
                limit={FIELD_LIMITS.TEXT}
                onChange={onChange}
              />
            </div>
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
              <SelectInput
                options={parentOrganizationOptions.map((org) => ({
                  value: org.org_id,
                  label: org.org_name,
                }))}
                className={" mr-1 w-full"}
                defaultValue={
                  existingOrganization &&
                  organization.parent_organization_id &&
                  parentOrganizationOptions
                    ? parentOrganizationOptions.find(
                        (org) =>
                          org.org_id === organization.parent_organization_id,
                      ) // Using find to get the matching org object
                      ? {
                          value: organization.parent_organization_id,
                          label:
                            parentOrganizationOptions.find(
                              (org) =>
                                org.org_id ===
                                organization.parent_organization_id,
                            )?.org_name || "",
                        }
                      : undefined
                    : undefined
                }
                isDisabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                isLoading={false}
                errors=""
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                onChange={onSelectParentOrg}
                name={t("Parent Organization")}
                searchBar={false}
              />
              {organization.parent_organization_id && (
                <Tooltip
                  title={t(
                    "If checked, each user in the Parent Organization will be assigned to this Organization as a Viewer, unless they already have a role for this Organization.",
                  )}
                  placement="left"
                  arrow
                  slotProps={{ tooltip: { sx: { fontSize: "1em" } } }}
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 600 }}
                >
                  <div className="flex flex-row items-start">
                    <input
                      className="mt-1.5 accent-storm-blue dark:accent-sky-blue cursor-pointer transition duration-300"
                      type="checkbox"
                      name="inherit_permissions"
                      checked={organization.inherit_permissions}
                      onChange={onChange}
                      disabled={
                        canEditOrgDetails || !existingOrganization
                          ? false
                          : true
                      }
                    />
                    <div className="flex flex-col">
                      <label className="inline-block mx-2 text-primary-off-black dark:text-primary-off-white transition duration-300">
                        {t("Inherit User Roles from Parent Organization")}
                      </label>
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex flex-wrap text-left -mx-3 mb-6">
            <div className={tailwindClassnames.detailsPageTextInputContainer}>
              <TextInput
                name="primary_contact_email"
                label={t("Primary Contact Email") + "*"}
                placeholder=""
                value={
                  organization.primary_contact_email
                    ? organization.primary_contact_email
                    : ""
                }
                error={errors.primary_contact_email}
                disabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                className={
                  canEditOrgDetails || !existingOrganization
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                type="text"
                initialFocus={false}
                limit={FIELD_LIMITS.EMAIL}
                onChange={onChange}
              />
            </div>
            <div className={tailwindClassnames.detailsPageTextInputContainer}>
              <TextInput
                name="primary_contact_phone"
                label={t("Primary Contact Phone Number")}
                placeholder=""
                value={
                  organization.primary_contact_phone
                    ? organization.primary_contact_phone
                    : ""
                }
                error={errors.primary_contact_phone}
                disabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                className={
                  canEditOrgDetails || !existingOrganization
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                type="text"
                initialFocus={false}
                limit={FIELD_LIMITS.PHONE}
                onChange={onChange}
              />
            </div>
          </div>
          <div className="flex flex-wrap text-left -mx-3 mb-6">
            <div className={tailwindClassnames.detailsPageTextInputContainer}>
              <TextInput
                name="address"
                label={t("Address")}
                placeholder={t("Enter your address...")}
                value={organization.address ? organization.address : ""}
                error={errors.address}
                disabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                className={
                  canEditOrgDetails || !existingOrganization
                    ? tailwindClassnames.detailsPageTextArea
                    : tailwindClassnames.detailsPageTextAreaDisabled
                }
                type="text"
                initialFocus={false}
                onChange={onChange}
                softLimit={FIELD_LIMITS.ADDRESS}
                rows={4}
              />
            </div>
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
              <SelectInput
                options={countries.map((country) => ({
                  value: country.code,
                  label: country.name,
                }))}
                className={" mr-1 w-full"}
                defaultValue={
                  defaultCountry
                    ? {
                        value: defaultCountry.code,
                        label: defaultCountry.name,
                      }
                    : undefined
                }
                isDisabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                errors={errors.country_id}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                onChange={onSelectCountry}
                name={t("Country") + "*"}
                searchBar={false}
              />
            </div>
          </div>
          <div className="flex flex-wrap text-left -mx-3 mb-6">
            <div className={tailwindClassnames.detailsPageTextInputContainer}>
              <TextInput
                name="website"
                label={t("Website")}
                placeholder=""
                value={organization.website ? organization.website : ""}
                error={errors.website}
                disabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                className={
                  canEditOrgDetails || !existingOrganization
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                type="text"
                initialFocus={false}
                limit={FIELD_LIMITS.TEXT}
                onChange={onChange}
              />
            </div>
          </div>
          <div className="flex flex-wrap text-left -mx-3 mb-6">
            <div className={tailwindClassnames.detailsPageTextInputContainer}>
              <TextInput
                name="comments"
                label={t("Comments")}
                placeholder=""
                value={organization.comments ? organization.comments : ""}
                error={errors.comments}
                disabled={
                  canEditOrgDetails || !existingOrganization ? false : true
                }
                className={
                  canEditOrgDetails || !existingOrganization
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                type="text"
                initialFocus={false}
                onChange={onChange}
                softLimit={FIELD_LIMITS.NARRATIVE}
                rows={2}
              />
            </div>
          </div>
        </section>

        <div className={tailwindClassnames.formActionBar}>
          <button
            className={tailwindClassnames.btnSecondary}
            type="button"
            onClick={() => onCancel()}
          >
            {t("Cancel")}
          </button>
          <button
            className={
              canEditOrgDetails || !existingOrganization
                ? `relative ${tailwindClassnames.btnPrimary}`
                : "hidden"
            }
            disabled={saveDisabled || isSaving}
            type="submit"
            onClick={onSave}
          >
            {/* Label stays in layout while saving so the button keeps its
                width; currentColor spinner reads on the fill in both
                themes. */}
            <span className={isSaving ? "invisible" : ""}>{t("Save")}</span>
            {isSaving && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationDetailsOverview;
